import { Box, Skeleton, Typography, pn } from '@compass/core';
import { Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { DevExpressReport } from 'components';
import { useParticipantsData } from '../contexts/ParticipantsDataContext';
import { DashboardCard } from '../components/DashboardCard';
import { ParticipantsFilterControls } from './ParticipantsFilterControls';
import { ParticipantStatusRow } from '../types/ParticipantStatusData';

const ParticipantsTableContent = () => {
    const { tableData, loading, error } = useParticipantsData();
    const appTenantName = useSelector((state: RootState) => state.app.tenantName);

    if (loading) {
        return <Skeleton variant="rounded" height={300} />;
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!tableData.length) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography variant="body2" color="text.secondary">
                    No participants found for selected filters
                </Typography>
            </Box>
        );
    }

    return (
        <DevExpressReport
            dataSource={tableData}
            fullPageSize
            showColumnChooser={false}
            exportFormats={['pdf', 'excel', 'csv']}
            onExporting={() => ({
                header: 'Participants Assisted',
                filename: 'Participants_Assisted',
                tenantName: appTenantName,
            })}
        >
            <Column
                dataField={pn<ParticipantStatusRow>('participantStatusName')}
                caption="Participation Status"
                width="50%"
            />
            <Column
                dataField={pn<ParticipantStatusRow>('participantCount')}
                caption="Count"
                width="25%"
                alignment="right"
            />
            <Column
                dataField={pn<ParticipantStatusRow>('percentage')}
                caption="Percentage"
                width="25%"
                alignment="right"
                customizeText={(cellInfo) => `${cellInfo.value?.toFixed(1)}%`}
            />
            <Summary>
                <TotalItem
                    column={pn<ParticipantStatusRow>('participantStatusName')}
                    customizeText={() => 'Total'}
                />
                <TotalItem
                    column={pn<ParticipantStatusRow>('participantCount')}
                    summaryType="sum"
                    customizeText={(cellInfo) => String(cellInfo.value)}
                />
                <TotalItem
                    column={pn<ParticipantStatusRow>('percentage')}
                    customizeText={() => '100.0%'}
                />
            </Summary>
        </DevExpressReport>
    );
};

export const ParticipantsTableCard = () => (
    <DashboardCard title="Participants Assisted">
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <ParticipantsFilterControls />
            <ParticipantsTableContent />
        </Box>
    </DashboardCard>
);